<template>
  <el-card class="box-card full-screen">
    <div ref="pageHead" class="table-page-head">
      <el-form :inline="true" :model="queryForm" size="small">
        <el-form-item :label="$t('customerName')">
          <el-input
            v-model="queryForm.customerName"
            clearable
            style="width: 160px"
          />
        </el-form-item>
        <el-form-item :label="$t('phone')">
          <el-input
            v-model="queryForm.phoneNo"
            clearable
            style="width: 150px"
          />
        </el-form-item>
        <el-form-item :label="$t('expenseType')">
          <el-select
            v-model="queryForm.expenseType"
            clearable
            :placeholder="$t('all')"
            style="width: 220px"
          >
            <el-option
              v-for="t in expenseTypes"
              :key="t.id"
              :label="$t(t.name)"
              :value="t.name"
            />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('status')">
          <el-select
            v-model="queryForm.status"
            clearable
            :placeholder="$t('all')"
            style="width: 140px"
          >
            <el-option :label="$t('checkPending')" :value="0" />
            <el-option :label="$t('getApproved')" :value="1" />
            <el-option :label="$t('notApproved')" :value="2" />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('date')">
          <el-date-picker
            v-model="queryForm.date"
            type="daterange"
            range-separator="~"
            :start-placeholder="$t('startDate')"
            :end-placeholder="$t('endDate')"
            value-format="yyyy-MM-dd"
            unlink-panels
            style="width: 240px"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="isLoading" @click="query">
            <i class="el-icon-search"></i> {{ $t("query") }}
          </el-button>
          <el-button @click="onReset">{{ $t("reset") }}</el-button>
        </el-form-item>
      </el-form>
    </div>

    <div
      class="data-table"
      :style="{
        top: queryTableHeight,
        height: `calc(100% - ${queryTableHeight} - 70px)`
      }"
    >
      <el-table
        :data="table.records"
        v-loading="isLoading"
        size="mini"
        height="100%"
        element-loading-background="rgba(255,255,255,0.4)"
      >
        <el-table-column type="index" align="center" fixed="left" width="50" />

        <el-table-column :label="$t('creationTime')" width="150">
          <template slot-scope="s">{{
            $manba(s.row.createTime).format("ymdhms")
          }}</template>
        </el-table-column>

        <el-table-column :label="$t('beneficiary')" min-width="200">
          <template slot-scope="s">
            {{ s.row.customerName }}
            <div class="text-muted" style="font-size: 12px">
              {{ s.row.customerCode }}
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="phoneNo" :label="$t('phone')" width="140" />

        <el-table-column :label="$t('amount')" width="150" align="right">
          <template slot-scope="s">
            <span class="franc">FCFA</span> {{ fmt(s.row.amount) }}
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('expenseType')"
          min-width="200"
          show-overflow-tooltip
        >
          <template slot-scope="s">{{ $t(s.row.expenseType) }}</template>
        </el-table-column>

        <el-table-column :label="$t('status')" width="120">
          <template slot-scope="s">
            <el-tag size="mini" :type="statusTag(s.row.status)">
              {{ statusLabel(s.row.status) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column :label="$t('createId')" min-width="140">
          <template slot-scope="s">
            {{ s.row.createName }} {{ s.row.createSurname }}
          </template>
        </el-table-column>

        <el-table-column :label="$t('verifyUser')" min-width="140">
          <template slot-scope="s">
            {{ s.row.verifyUser || "—" }}
            <div
              class="text-muted"
              style="font-size: 12px"
              v-if="s.row.verifyTime"
            >
              {{ $manba(s.row.verifyTime).format("ymdhms") }}
            </div>
          </template>
        </el-table-column>

        <el-table-column :label="$t('extend')" fixed="right" width="250">
          <template slot-scope="s">
            <el-button size="mini" @click="openDetail(s.row)">{{
              $t("details")
            }}</el-button>
            <template v-if="s.row.status == 0">
              <el-button
                size="mini"
                type="success"
                @click="openAction(s.row, 'approve')"
              >
                {{ $t("approve") }}
              </el-button>
              <el-button
                size="mini"
                type="danger"
                @click="openAction(s.row, 'reject')"
              >
                {{ $t("reject") }}
              </el-button>
            </template>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <el-pagination
      background
      layout="total,sizes,prev,pager,next,jumper"
      :total="table.total || 0"
      :page-sizes="[20, 30, 50, 100]"
      :page-size="pageSize"
      :current-page="currentPage"
      @size-change="setPageSize"
      @current-change="setCurrentChange"
    />

    <el-drawer
      :title="$t('operatingPayment')"
      :visible.sync="detailDrawer"
      direction="rtl"
      :modal="false"
      :destroy-on-close="true"
      :size="800"
    >
      <div style="padding: 20px" v-loading="detailLoading">
        <template v-if="detail">
          <table class="detail-table">
            <tr>
              <th>{{ $t("beneficiary") }}</th>
              <td>{{ detail.customerName }} ({{ detail.customerCode }})</td>
            </tr>
            <tr>
              <th>{{ $t("phone") }}</th>
              <td>{{ detail.phoneNo }}</td>
            </tr>
            <tr>
              <th>{{ $t("account") }}</th>
              <td>{{ detail.cardNo }}</td>
            </tr>
            <tr>
              <th>{{ $t("amount") }}</th>
              <td>
                <b>FCFA {{ fmt(detail.amount) }}</b>
              </td>
            </tr>
            <tr>
              <th>{{ $t("expenseType") }}</th>
              <td>{{ $t(detail.expenseType) }}</td>
            </tr>
            <tr>
              <th>{{ $t("reason") }}</th>
              <td>{{ detail.reason || "—" }}</td>
            </tr>
            <tr>
              <th>{{ $t("remark") }}</th>
              <td>{{ detail.remark || "—" }}</td>
            </tr>
            <tr>
              <th>{{ $t("status") }}</th>
              <td>
                <el-tag size="mini" :type="statusTag(detail.status)">
                  {{ statusLabel(detail.status) }}</el-tag
                >
              </td>
            </tr>
            <tr v-if="detail.verifyRemark">
              <th>{{ $t("checkRemark") }}</th>
              <td>{{ detail.verifyRemark }}</td>
            </tr>
          </table>

          <el-divider content-position="left">{{
            $t("paymentSources")
          }}</el-divider>

          <el-table :data="detail.sources || []" size="mini" border>
            <el-table-column
              prop="orgName"
              :label="$t('org')"
              min-width="160"
            />
            <el-table-column :label="$t('type')" width="110">
              <template slot-scope="s">
                <el-tag size="mini" :type="s.row.orgType == 1 ? '' : 'info'">
                  {{
                    s.row.orgType == 1 ? $t("platform") : $t("directSaleStore")
                  }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column :label="$t('amount')" width="120" align="right">
              <template slot-scope="s">{{ fmt(s.row.amount) }}</template>
            </el-table-column>
            <el-table-column
              :label="$t('balanceBefore')"
              width="130"
              align="right"
            >
              <template slot-scope="s">
                {{
                  s.row.balanceBefore == null ? "—" : fmt(s.row.balanceBefore)
                }}
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('balanceAfter')"
              width="130"
              align="right"
            >
              <template slot-scope="s">
                {{ s.row.balanceAfter == null ? "—" : fmt(s.row.balanceAfter) }}
              </template>
            </el-table-column>
          </el-table>

          <div v-if="detail.status == 0" class="op-current">
            <div class="op-current-title">{{ $t("currentBalances") }}</div>
            <div
              v-for="s in detail.sources || []"
              :key="s.id"
              class="op-current-row"
            >
              <span>{{ s.orgName }}</span>
              <span
                :class="
                  currentBalance(s.orgInfoId) >= num(s.amount) ? 'ok' : 'ko'
                "
              >
                FCFA {{ fmt(currentBalance(s.orgInfoId)) }}
                {{ currentBalance(s.orgInfoId) >= num(s.amount) ? "✅" : "❌" }}
              </span>
            </div>
          </div>

          <div class="op-drawer-footer" v-if="detail.status == 0">
            <el-button type="danger" @click="openAction(detail, 'reject')">
              {{ $t("reject") }}
            </el-button>
            <el-button
              type="success"
              :disabled="!allSourcesSufficient"
              @click="openAction(detail, 'approve')"
            >
              {{ $t("approve") }}
            </el-button>
          </div>
        </template>
      </div>
    </el-drawer>

    <el-dialog
      :title="actionMode === 'approve' ? $t('approve') : $t('reject')"
      :visible.sync="actionDialog"
      width="460px"
      :close-on-click-modal="false"
      :modal-append-to-body="false"
    >
      <div v-if="actionRow" class="op-action-summary">
        {{ actionRow.customerName }} — <b>FCFA {{ fmt(actionRow.amount) }}</b>
      </div>
      <el-alert
        v-if="actionMode === 'approve'"
        :title="$t('operatingApproveWarning')"
        type="warning"
        :closable="false"
        show-icon
        style="margin: 10px 0"
      />
      <el-input
        type="textarea"
        :rows="3"
        v-model="actionRemark"
        :placeholder="
          actionMode === 'reject'
            ? $t('rejectReasonRequired')
            : $t('checkRemark')
        "
      />
      <span slot="footer">
        <el-button @click="actionDialog = false">{{ $t("cancel") }}</el-button>
        <el-button
          :type="actionMode === 'approve' ? 'success' : 'danger'"
          :loading="actionLoading"
          @click="submitAction"
        >
          {{ $t("confirm") }}
        </el-button>
      </span>
    </el-dialog>
  </el-card>
</template>

<script>
import operatingPayment from "@/api/operatingPayment";
import expense from "@/api/expense";

export default {
  name: "operatingPaymentList",
  data() {
    return {
      queryForm: {
        customerName: "",
        phoneNo: "",
        expenseType: "",
        status: null,
        date: null
      },
      queryTableHeight: String,
      table: { records: [], total: 0 },
      pageSize: 20,
      currentPage: 1,
      isLoading: false,
      expenseTypes: [],

      balances: [],

      detailDrawer: false,
      detailLoading: false,
      detail: null,

      actionDialog: false,
      actionMode: "approve",
      actionRow: null,
      actionRemark: "",
      actionLoading: false
    };
  },
  computed: {
    allSourcesSufficient() {
      const src = (this.detail && this.detail.sources) || [];
      return (
        src.length > 0 &&
        src.every(s => this.currentBalance(s.orgInfoId) >= this.num(s.amount))
      );
    }
  },
  methods: {
    num(v) {
      return parseFloat(String(v == null ? "" : v).replace(/\s/g, "")) || 0;
    },
    fmt(n) {
      return Number(n || 0).formatMoney(0);
    },
    statusLabel(s) {
      return s == 0
        ? this.$t("checkPending")
        : s == 1
        ? this.$t("getApproved")
        : this.$t("notApproved");
    },
    statusTag(s) {
      return s == 0 ? "warning" : s == 1 ? "success" : "danger";
    },

    getList() {
      this.isLoading = true;
      const p = { current: this.currentPage, size: this.pageSize };
      const f = this.queryForm;
      if (f.customerName) p.customerName = f.customerName.trim();
      if (f.phoneNo) p.phoneNo = f.phoneNo.replace(/\s/g, "");
      if (f.expenseType) p.expenseType = f.expenseType;
      if (f.status !== null && f.status !== "") p.status = f.status;
      if (f.date && f.date.length === 2) {
        p.startTime = f.date[0];
        p.endTime = f.date[1];
      }
      operatingPayment.page({
        param: p,
        success: res => {
          this.isLoading = false;
          this.table =
            res.code === 200 && res.data ? res.data : { records: [], total: 0 };
        },
        failing: () => {
          this.isLoading = false;
        }
      });
    },
    query() {
      this.currentPage = 1;
      this.getList();
    },
    onReset() {
      this.queryForm = {
        customerName: "",
        phoneNo: "",
        expenseType: "",
        status: null,
        date: null
      };
      this.query();
    },
    setPageSize(v) {
      this.pageSize = v;
      this.getList();
    },
    setCurrentChange(v) {
      this.currentPage = v;
      this.getList();
    },

    loadBalances() {
      operatingPayment.commissionBalances({
        param: {},
        success: res => {
          if (res.code === 200 && res.data) this.balances = res.data.list || [];
        }
      });
    },
    currentBalance(orgInfoId) {
      const b = this.balances.find(x => x.orgInfoId === orgInfoId);
      return b ? this.num(b.accountAmount) : 0;
    },

    openDetail(row) {
      this.detail = null;
      this.detailDrawer = true;
      this.detailLoading = true;
      this.loadBalances();
      operatingPayment.detail({
        param: { id: row.id },
        success: res => {
          this.detailLoading = false;
          if (res.code === 200) this.detail = res.data;
        },
        failing: () => {
          this.detailLoading = false;
        }
      });
    },

    openAction(row, mode) {
      this.actionRow = row;
      this.actionMode = mode;
      this.actionRemark = "";
      this.actionDialog = true;
    },

    submitAction() {
      const remark = this.actionRemark.trim();
      if (this.actionMode === "reject" && !remark) {
        this.$message.warning(this.$t("rejectReasonRequired"));
        return;
      }
      this.actionLoading = true;
      const call =
        this.actionMode === "approve"
          ? operatingPayment.approve
          : operatingPayment.reject;

      call({
        param: { id: this.actionRow.id, verifyRemark: remark },
        success: res => {
          this.actionLoading = false;
          if (res.code === 200) {
            this.$message.success(this.$t("operateSuccessfully"));
            this.actionDialog = false;
            this.detailDrawer = false;
            this.getList();
          } else {
            this.$message.warning(this.errorMessage(res.msg));
            if (this.detailDrawer) this.loadBalances();
          }
        },
        failing: () => {
          this.actionLoading = false;
          this.$message.error(this.$t("networkError"));
        }
      });
    },

    errorMessage(msg) {
      if (!msg) return this.$t("operationFailure");
      const i = msg.indexOf("insufficient:");
      if (i >= 0)
        return this.$t("insufficientSource", { org: msg.substring(i + 13) });
      if (msg.indexOf("already processed") >= 0)
        return this.$t("alreadyProcessed");
      return this.$t(msg);
    }
  },
  mounted() {
    this.queryTableHeight = this.$refs.pageHead.offsetHeight + 15 + "px";
    expense.getAllExpenseType({
      param: {},
      success: res => {
        if (res.code === 200) this.expenseTypes = res.data || [];
      }
    });
    this.getList();
  }
};
</script>

<style lang="less" scoped>
.detail-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th,
  td {
    border: 1px solid #ebeef5;
    padding: 9px 12px;
    text-align: left;
    vertical-align: top;
  }
  th {
    width: 180px;
    background: #fafafa;
    font-weight: 600;
    color: #606266;
  }
}
.op-current {
  margin-top: 16px;
  padding: 10px 14px;
  background: #f5f7fa;
  border-radius: 6px;
  font-size: 13px;
}
.op-current-title {
  font-weight: 600;
  margin-bottom: 6px;
  color: #606266;
}
.op-current-row {
  display: flex;
  justify-content: space-between;
  padding: 3px 0;
  .ok {
    color: #67c23a;
    font-weight: 600;
  }
  .ko {
    color: #f56c6c;
    font-weight: 600;
  }
}
.op-action-summary {
  font-size: 14px;
}

.op-drawer-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding-top: 14px;
  border-top: 1px solid #ebeef5;
}
</style>
